/**
 * useMediaPermissions — le micro et la caméra sont-ils autorisés à l'app (ou au site) ?
 *
 * La lecture de l'ancien `usePermissions` (`common/livekit/components/PermissionBanner.js`) : sur le
 * web, `navigator.permissions` et ses changements ; sur mobile, les permissions d'Expo, relues au
 * retour au premier plan (l'élève a pu les changer dans les réglages). Deux écarts voulus :
 *  - une lecture impossible (Firefox ne connaît pas la permission `camera`) dit « on ne sait pas »
 *    (`null`), pas « refusé » — l'ancien posait alors un « ! » sur un micro qui marchait ;
 *  - **seul un refus est un refus** : l'état « on demandera » (`prompt` du navigateur,
 *    `undetermined` d'Expo) dit « on ne sait pas ». Safari reste souvent à `prompt` micro ouvert :
 *    l'ancien y marquait le micro bloqué, et « Un problème ? » disait « Ton navigateur bloque ton
 *    micro » à un micro qui marchait.
 *
 * Une lecture impossible laisse un fil d'Ariane : rien n'est en panne, mais on saura pourquoi la
 * barre ne dit rien.
 */
import { getRecordingPermissionsAsync } from "expo-audio"
import { Camera } from "expo-camera"
import { useEffect, useState } from "react"
import { AppState, Platform } from "react-native"

import { bugsnagClient } from "../../shared/bugsnagClient"

export type MediaPermissions = { microphone: boolean | null; camera: boolean | null }

const UNKNOWN: MediaPermissions = { microphone: null, camera: null }

type PermissionStatusLike = {
    state: string
    addEventListener?: (type: "change", listener: () => void) => void
    removeEventListener?: (type: "change", listener: () => void) => void
}

/** `granted` : autorisé ; `denied` : refusé ; tout autre état (`prompt`, `undetermined`) : on ne sait pas. */
export function permissionAllowed(state: string | null | undefined): boolean | null {
    if (state === "granted") return true
    if (state === "denied") return false
    return null
}

const reasonOf = (cause: unknown) => (cause instanceof Error ? cause.name : typeof cause)

async function queryWeb(name: "microphone" | "camera"): Promise<PermissionStatusLike | null> {
    const permissions = (navigator as unknown as { permissions?: { query: (d: { name: string }) => Promise<unknown> } })
        ?.permissions
    if (permissions?.query == null) return null
    try {
        return (await permissions.query({ name })) as PermissionStatusLike
    } catch (cause) {
        // Le navigateur ne connaît pas cette permission : on ne sait pas.
        bugsnagClient.leaveBreadcrumb("live: permission unreadable", { name, reason: reasonOf(cause) })
        return null
    }
}

export function useMediaPermissions(): MediaPermissions {
    const [permissions, setPermissions] = useState<MediaPermissions>(UNKNOWN)

    useEffect(() => {
        let mounted = true
        if (Platform.OS === "web") {
            const listening: { status: PermissionStatusLike; listener: () => void }[] = []
            void Promise.all([queryWeb("microphone"), queryWeb("camera")]).then(([mic, cam]) => {
                if (!mounted) return
                const read = () =>
                    setPermissions({
                        microphone: mic != null ? permissionAllowed(mic.state) : null,
                        camera: cam != null ? permissionAllowed(cam.state) : null,
                    })
                read()
                for (const status of [mic, cam]) {
                    if (status?.addEventListener == null) continue
                    status.addEventListener("change", read)
                    listening.push({ status, listener: read })
                }
            })
            return () => {
                mounted = false
                for (const { status, listener } of listening) status.removeEventListener?.("change", listener)
            }
        }

        const check = () => {
            Promise.all([getRecordingPermissionsAsync(), Camera.getCameraPermissionsAsync()])
                .then(([mic, cam]) => {
                    if (mounted)
                        setPermissions({
                            microphone: permissionAllowed(mic.status),
                            camera: permissionAllowed(cam.status),
                        })
                })
                .catch((cause: unknown) => {
                    // Une lecture impossible ne dit rien du micro : on ne sait pas.
                    bugsnagClient.leaveBreadcrumb("live: permission unreadable", { reason: reasonOf(cause) })
                    if (mounted) setPermissions(UNKNOWN)
                })
        }
        check()
        const subscription = AppState.addEventListener("change", (state) => {
            if (state === "active") check()
        })
        return () => {
            mounted = false
            subscription.remove()
        }
    }, [])

    return permissions
}
